import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { visitApi, bookingsApi, systemApi } from '../utils/api';
import { getImageUrl } from '../config/imageConfig';
import CarouselBanner from './common/CarouselBanner';
import './Visit.css';

// 导入参观游览图片
import cgylImg from '../assets/cgyl/cgylt.png';

function Visit() {
  const [selectedTab, setSelectedTab] = useState('information');
  const [showImageModal, setShowImageModal] = useState(false);
  
  // API数据状态
  const [visitInfo, setVisitInfo] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    visitDate: '',
    visitTime: '',
    visitorCount: 1,
    contactName: '',
    contactPhone: '',
    specialRequests: ''
  });
  
  // 获取参观信息
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [infoData, routesData] = await Promise.all([
          systemApi.getBusinessHours(),
          bookingsApi.getRoutes()
        ]);
        setVisitInfo(infoData.data);
        setRoutes(routesData.data || []);
      } catch (err) {
        console.error('Error fetching visit data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // 处理预约表单变更
  const handleBookingFormChange = (field, value) => {
    setBookingForm(prev => ({ ...prev, [field]: value }));
  };
  
  // 提交参观预约
  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    try {
      setBookingLoading(true);
      await bookingsApi.createBooking({
        type: 'visit',
        ...bookingForm
      });
      alert('参观预约提交成功！我们会尽快与您联系确认。');
      // 重置表单
      setBookingForm({
        visitDate: '',
        visitTime: '',
        visitorCount: 1,
        contactName: '',
        contactPhone: '',
        specialRequests: ''
      });
    } catch (err) {
      console.error('Error submitting booking:', err);
      alert('预约提交失败，请稍后重试');
    } finally {
      setBookingLoading(false);
    }
  };

  // 图片资源 - 使用全局配置处理URL
  const visitHeroImg = getImageUrl('https://images.unsplash.com/photo-1508025750423-d8abd0f22388?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2342&q=80');
  const gardenMapImg = getImageUrl('https://images.unsplash.com/photo-1588421357574-87938a86fa28?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80');
  const tourImg = getImageUrl('https://images.unsplash.com/photo-1558522195-e1201b090344?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80');
  
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  // 处理导游图点击放大
  const handleImageClick = () => {
    setShowImageModal(true);
  };

  // 关闭图片模态框
  const handleCloseImageModal = () => {
    setShowImageModal(false);
  };

  return (
    <div>
      {/* Hero Section - 使用轮播图组件 */}
      <CarouselBanner 
        categoryNameEn="XLHY"
        defaultImage={visitHeroImg}
        defaultTitle="参观游览"
        defaultSubtitle="了解我们的开放时间、票价和参观须知，计划您的完美行程。"
        className="hero-section"
      />
      
      <div className="visit-container">
        {/* 参观信息 */}
        <section className="visit-info-section">
          <h2 className="section-title">参观信息</h2>
          <p className="section-subtitle">了解我们的开放时间、票价和参观须知，计划您的完美行程。</p>
          
          {/* 开放时间 */}
          <div className="info-block">
            <h3 className="info-title">开放时间</h3>
            <div className="time-grid">
              <div className="time-item">
                <h4>春季和夏季 (3月15日-10月31日)</h4>
                <p>周一至周日: 8:30 - 17:30 (最后入园时间: 16:30)</p>
              </div>
              <div className="time-item">
                <h4>秋季和冬季 (11月1日-3月14日)</h4>
                <p>周一至周日: 9:00 - 17:00 (最后入园时间: 16:00)</p>
              </div>
              <div className="time-item">
                <h4>年节假期</h4>
                <p>春节、清明节、劳动节、国庆节等重要节假日可能会调整开放时间，请关注官方公告。</p>
              </div>
            </div>
            <div className="closed-info">
              <h4>闭园信息</h4>
              <p>每年检修期间会暂停开放，具体时间另行通知。</p>
            </div>
          </div>
          
          {/* 门票信息 */}
          <div className="info-block">
            <h3 className="info-title">门票信息</h3>
            <div className="ticket-grid">
              <div className="ticket-item">
                <h4>标准票</h4>
                <p className="price">￥60/人</p>
                <p className="desc">包含入园及常设展区参观</p>
              </div>
              <div className="ticket-item">
                <h4>优惠票</h4>
                <p className="price">￥30/人</p>
                <p className="desc">适用于学生、老年人(65岁以上)、残障人士</p>
              </div>
              <div className="ticket-item">
                <h4>家庭票</h4>
                <p className="price">￥140/套</p>
                <p className="desc">2大2小（12岁以下儿童）</p>
              </div>
            </div>
          </div>
          
          {/* 参观须知 */}
          <div className="info-block">
            <h3 className="info-title">参观须知</h3>
            <ul className="rules-list">
              <li>✓ 爱护园内花草树木和设施，不攀折植物，不攀爬围栏。</li>
              <li>✓ 不随地吐痰、不乱扔垃圾，做文明游客。</li>
              <li>✓ 请勿携带宠物入园（导盲犬除外）。</li>
              <li>✓ 园内禁止吸烟，设有专门吸烟区。</li>
              <li>✓ 请勿在花园内使用无人机等航拍设备。</li>
              <li>✓ 专业摄影（包括商业摄影、婚纱摄影等）需提前申请。</li>
              <li>✓ 如有任何问题，可咨询园内工作人员或拨打服务热线：0792-12345678。</li>
            </ul>
          </div>
          
          {/* 交通指南 */}
          <div className="info-block">
            <h3 className="info-title">交通指南</h3>
            <div className="transport-grid">
              <div className="transport-item">
                <h4>公共交通</h4>
                <p>乘坐公交15路、27路、33路至"庐山植物园站"下车，步行约3分钟可达。</p>
              </div>
              <div className="transport-item">
                <h4>自驾路线</h4>
                <p>导航至"庐山杏林中药植物研究所"，园内设有停车场，收费标准：小型车15元/次，大型车30元/次。</p>
              </div>
              <div className="transport-item">
                <h4>出租车</h4>
                <p>从庐山火车站约15分钟车程，车费约25元；从九江市区约30分钟车程，车费约50元。</p>
              </div>
            </div>
          </div>
        </section>

        {/* 园区概览 */}
        <section className="garden-overview-section">
          <div className="overview-content">
            <div className="overview-text">
              <h2 className="section-title">园区概览</h2>
              <p className="section-subtitle">
                庐山杏林中药植物研究所坐落在风景秀丽的庐山脚下，占地面积广阔，园区环境优美，
                集科研、教育、旅游于一体，是了解中药植物文化的理想场所。
              </p>
              <div className="overview-features">
                <div className="feature-item">
                  <h4>🌿 丰富的植物资源</h4>
                  <p>园区内收集保存了千余种药用植物，涵盖了中国传统中药的主要品种</p>
                </div>
                <div className="feature-item">
                  <h4>🏛️ 现代化设施</h4>
                  <p>配备先进的科研设施、展示馆、标本馆和教育培训中心</p>
                </div>
                <div className="feature-item">
                  <h4>🌸 四季皆景</h4>
                  <p>不同季节都有独特的植物景观，春花秋果，四季更迭，美不胜收</p>
                </div>
                <div className="feature-item">
                  <h4>📚 科普教育</h4>
                  <p>提供专业的导览服务和科普教育，让您深入了解植物的奥秘</p>
                </div>
              </div>
            </div>
            <div className="overview-image">
              <img 
                src={cgylImg} 
                alt="庐山杏林中药植物研究所园区导游图" 
                className="garden-overview-img" 
                onClick={handleImageClick}
                title="点击查看大图"
              />
              <div className="image-caption">
                <p>庐山杏林中药植物研究所园区导游图 <span className="click-hint">（点击查看大图）</span></p>
              </div>
            </div>
          </div>
        </section>
          
        {/* 游览路线面板 */}
        {selectedTab === 'routes' && (
            <div className="visit-panel">
              <h2 className="page-title">游览路线</h2>
              <p className="page-subtitle">根据您的兴趣和时间，选择最适合的游览路线，探索杏林花园。</p>
              
              <div className="map-container">
                <img src={gardenMapImg} alt="杏林花园地图" className="garden-map" />
              </div>
              
              <div className="routes-section">
                <h3>推荐路线</h3>
                
                <div className="route-card">
                  <div className="route-header">
                    <div className="route-title">
                      <h4>精华游</h4>
                      <span className="route-duration">约1.5小时</span>
                    </div>
                    <div className="route-tags">
                      <span className="route-tag">初访必选</span>
                      <span className="route-tag">精华景点</span>
                    </div>
                  </div>
                  <div className="route-body">
                    <div className="route-description">
                      <p>这条路线包含杏林花园最具代表性的景点，适合首次来访或时间有限的游客。</p>
                    </div>
                    <div className="route-stops">
                      <h5>游览路线</h5>
                      <ol>
                        <li>正门入口 → 游客中心</li>
                        <li>四季药园（春季区域）</li>
                        <li>传统中药展示区</li>
                        <li>珍稀植物保育区</li>
                        <li>药用植物展示馆</li>
                        <li>杏林茶室休息</li>
                        <li>出口</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="route-card">
                  <div className="route-header">
                    <div className="route-title">
                      <h4>科研探索</h4>
                      <span className="route-duration">约2.5小时</span>
                    </div>
                    <div className="route-tags">
                      <span className="route-tag">科普教育</span>
                      <span className="route-tag">深度了解</span>
                    </div>
                  </div>
                  <div className="route-body">
                    <div className="route-description">
                      <p>这条路线侧重于了解研究所的科研工作和成果，适合对植物学和药用植物研究感兴趣的访客。</p>
                    </div>
                    <div className="route-stops">
                      <h5>游览路线</h5>
                      <ol>
                        <li>正门入口 → 游客中心</li>
                        <li>科研展示厅</li>
                        <li>植物标本馆</li>
                        <li>现代药物研发展示区</li>
                        <li>实验室开放区（需预约）</li>
                        <li>植物资源库</li>
                        <li>杏林书屋</li>
                        <li>出口</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="route-card">
                  <div className="route-header">
                    <div className="route-title">
                      <h4>全景游</h4>
                      <span className="route-duration">约4小时</span>
                    </div>
                    <div className="route-tags">
                      <span className="route-tag">全面体验</span>
                      <span className="route-tag">深度游</span>
                    </div>
                  </div>
                  <div className="route-body">
                    <div className="route-description">
                      <p>这条路线涵盖杏林花园的所有主要景点，适合希望全面了解植物园的访客，建议安排半天时间，中途可在园内餐厅用餐。</p>
                    </div>
                    <div className="route-stops">
                      <h5>游览路线</h5>
                      <ol>
                        <li>正门入口 → 游客中心</li>
                        <li>四季药园（全部四个季节区域）</li>
                        <li>传统中药展示区</li>
                        <li>珍稀植物保育区</li>
                        <li>杏林餐厅（午餐）</li>
                        <li>科研展示厅</li>
                        <li>植物标本馆</li>
                        <li>现代药物研发展示区</li>
                        <li>植物资源库</li>
                        <li>艺术馆</li>
                        <li>杏林书屋</li>
                        <li>杏林茶室休息</li>
                        <li>纪念品商店</li>
                        <li>出口</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="info-section">
                <h3>季节推荐</h3>
                <div className="season-recommendations">
                  <div className="season-card">
                    <h4>春季推荐</h4>
                    <ul>
                      <li>杜鹃花专区（3-4月）</li>
                      <li>春季药用花卉展示区</li>
                      <li>桃花林和樱花区</li>
                    </ul>
                  </div>
                  <div className="season-card">
                    <h4>夏季推荐</h4>
                    <ul>
                      <li>荷花池（7-8月）</li>
                      <li>夏季草药园</li>
                      <li>山谷溪流植物区</li>
                    </ul>
                  </div>
                  <div className="season-card">
                    <h4>秋季推荐</h4>
                    <ul>
                      <li>银杏大道（10-11月）</li>
                      <li>秋季果实展示区</li>
                      <li>彩叶植物园</li>
                    </ul>
                  </div>
                  <div className="season-card">
                    <h4>冬季推荐</h4>
                    <ul>
                      <li>冬季花房</li>
                      <li>松柏园</li>
                      <li>室内药用植物展示馆</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* 导览服务面板 */}
          {selectedTab === 'guide' && (
            <div className="visit-panel">
              <h2 className="page-title">导览服务</h2>
              <p className="page-subtitle">专业导览员带您深入了解植物知识，获取更丰富的参观体验。</p>
              
              <div className="guide-showcase">
                <div className="guide-image">
                  <img src={tourImg} alt="专业导览服务" />
                </div>
                <div className="guide-intro">
                  <h3>专业导览服务</h3>
                  <p>我们的导览员均具备植物学或相关专业背景，接受过专业培训，能够为您提供权威、详尽的植物知识和研究所信息。</p>
                  <p>通过导览服务，您将了解到：</p>
                  <ul>
                    <li>植物的科学分类、特性和用途</li>
                    <li>中医药植物的历史和应用</li>
                    <li>植物保护的意义和措施</li>
                    <li>研究所的科研成果和贡献</li>
                    <li>植物背后的文化故事和传说</li>
                  </ul>
                </div>
              </div>
              
              <div className="guide-options">
                <h3>导览服务类型</h3>
                <div className="guide-cards">
                  <div className="guide-card">
                    <h4>定时导览</h4>
                    <p><strong>时间：</strong>每日10:00、14:00</p>
                    <p><strong>集合地点：</strong>游客中心导览服务台</p>
                    <p><strong>时长：</strong>约90分钟</p>
                    <p><strong>费用：</strong>20元/人（需提前30分钟到导览服务台购票）</p>
                    <p><strong>路线：</strong>精华游览路线</p>
                    <p><strong>人数限制：</strong>每团不超过20人</p>
                  </div>
                  
                  <div className="guide-card">
                    <h4>专题导览</h4>
                    <p><strong>主题：</strong>药用植物专题、珍稀植物专题、季节植物专题</p>
                    <p><strong>时间：</strong>周六、周日11:00</p>
                    <p><strong>集合地点：</strong>游客中心导览服务台</p>
                    <p><strong>时长：</strong>约120分钟</p>
                    <p><strong>费用：</strong>30元/人（需提前一天预约）</p>
                    <p><strong>人数限制：</strong>每团不超过15人</p>
                  </div>
                  
                  <div className="guide-card">
                    <h4>私人定制导览</h4>
                    <p><strong>特点：</strong>根据您的兴趣和需求定制专属讲解</p>
                    <p><strong>时间：</strong>需提前3天预约，时间灵活</p>
                    <p><strong>语言：</strong>中文、英文</p>
                    <p><strong>时长：</strong>根据需求定制，一般为1-3小时</p>
                    <p><strong>费用：</strong>200元/小时（1-5人）</p>
                    <p><strong>适合人群：</strong>专业研究人员、摄影爱好者、VIP访客</p>
                  </div>
                </div>
              </div>
              
              <div className="guide-reservation">
                <h3>预约方式</h3>
                <div className="reservation-methods">
                  <div className="reservation-method">
                    <h4>电话预约</h4>
                    <p>预约热线：0792-12345678</p>
                    <p>服务时间：9:00-16:30（周一至周日）</p>
                  </div>
                  <div className="reservation-method">
                    <h4>在线预约</h4>
                    <p>通过官方网站预约：<a href="#">在线导览预约</a></p>
                    <p>通过微信公众号预约：扫描下方二维码，关注"庐山杏林"</p>
                  </div>
                  <div className="reservation-method">
                    <h4>现场预约</h4>
                    <p>前往游客中心导览服务台</p>
                    <p>定时导览可提前30分钟现场购票（视剩余名额情况）</p>
                  </div>
                </div>
                
                <div className="guide-form">
                  <h4>导览预约表单</h4>
                  <form onSubmit={handleSubmitBooking}>
                    <div className="form-group">
                      <label htmlFor="guideType">导览类型</label>
                      <select 
                        id="guideType" 
                        className="form-control"
                        value={bookingForm.guideType || ''}
                        onChange={(e) => handleBookingFormChange('guideType', e.target.value)}
                      >
                        <option value="">-- 请选择导览类型 --</option>
                        <option value="regular">定时导览</option>
                        <option value="thematic">专题导览</option>
                        <option value="private">私人定制导览</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="visitDate">参观日期</label>
                      <input 
                        type="date" 
                        id="visitDate" 
                        className="form-control"
                        value={bookingForm.visitDate}
                        onChange={(e) => handleBookingFormChange('visitDate', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="visitTime">参观时间</label>
                      <select 
                        id="visitTime" 
                        className="form-control"
                        value={bookingForm.visitTime}
                        onChange={(e) => handleBookingFormChange('visitTime', e.target.value)}
                      >
                        <option value="">-- 请选择时间 --</option>
                        <option value="10:00">10:00</option>
                        <option value="11:00">11:00 (仅周末专题导览)</option>
                        <option value="14:00">14:00</option>
                        <option value="custom">其他时间 (仅私人定制)</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="personCount">人数</label>
                      <input 
                        type="number" 
                        id="personCount" 
                        className="form-control" 
                        min="1" 
                        value={bookingForm.visitorCount}
                        onChange={(e) => handleBookingFormChange('visitorCount', parseInt(e.target.value))}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="contactName">联系人</label>
                      <input 
                        type="text" 
                        id="contactName" 
                        className="form-control" 
                        placeholder="请输入联系人姓名"
                        value={bookingForm.contactName}
                        onChange={(e) => handleBookingFormChange('contactName', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="contactPhone">联系电话</label>
                      <input 
                        type="tel" 
                        id="contactPhone" 
                        className="form-control" 
                        placeholder="请输入联系电话"
                        value={bookingForm.contactPhone}
                        onChange={(e) => handleBookingFormChange('contactPhone', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="specialRequest">特殊要求</label>
                      <textarea 
                        id="specialRequest" 
                        className="form-control" 
                        rows="3" 
                        placeholder="如有特殊需求，请在此说明"
                        value={bookingForm.specialRequests}
                        onChange={(e) => handleBookingFormChange('specialRequests', e.target.value)}
                      ></textarea>
                    </div>
                    
                    <button 
                      type="submit" 
                      className="form-submit"
                      disabled={bookingLoading}
                    >
                      {bookingLoading ? '提交中...' : '提交预约'}
                    </button>
                  </form>
                </div>
              </div>
              
            </div>
          )}
      </div>

      {/* 图片放大模态框 */}
      {showImageModal && (
        <div className="image-modal-overlay" onClick={handleCloseImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={handleCloseImageModal}>
              ×
            </button>
            <img 
              src={cgylImg} 
              alt="庐山杏林中药植物研究所园区导游图 - 大图"
              className="image-modal-img"
            />
            <div className="image-modal-caption">
              <h3>园区导游图</h3>
              <p>庐山杏林中药植物研究所园区详细导游图，展示各个功能区域和游览路线</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Visit;